import { ISP_ENABLED } from '@/pages/Admin/Isp/ispEnabled';
import { create } from 'zustand';

export type Surface = 'wifi' | 'voucher-store' | 'tab' | 'isp';

const STORAGE_KEY = 'smartfi-surface';

const readInitial = (): Surface => {
   if (typeof window === 'undefined') return 'wifi';
   const v = window.localStorage.getItem(STORAGE_KEY);
   // 🔴 A SAVED SURFACE MUST NOT SURVIVE THE FEATURE BEING TURNED OFF. ISP
   // Business ships to production dark, and anyone who used it on staging has
   // 'isp' in their browser. Honouring that in a build where the ISP routes are
   // not mounted would land them on a 404 with a sidebar full of dead links —
   // and there would be no way back, because the product switcher hides the
   // option too.
   if (v === 'isp' && !ISP_ENABLED) return 'wifi';
   if (v === 'wifi' || v === 'voucher-store' || v === 'tab' || v === 'isp')
      return v;
   return 'wifi';
};

interface State {
   surface: Surface;
   /** True once auto-detect has run (or been skipped because localStorage already had a value). */
   resolved: boolean;
   setSurface: (s: Surface) => void;
   markResolved: () => void;
}

export const useSurfaceStore = create<State>((set) => ({
   surface: readInitial(),
   resolved:
      typeof window !== 'undefined' &&
      window.localStorage.getItem(STORAGE_KEY) !== null,
   setSurface: (s) => {
      if (typeof window !== 'undefined') {
         window.localStorage.setItem(STORAGE_KEY, s);
      }
      set({ surface: s, resolved: true });
   },
   markResolved: () => set({ resolved: true }),
}));

/**
 * Infer the active surface from a URL. Returns null when the URL is
 * surface-agnostic (Account pages) so the caller keeps the current surface.
 */
export const surfaceFromUrl = (pathname: string): Surface | null => {
   // Future prefixed URLs (Phase B onward).
   if (pathname === '/wifi' || pathname.startsWith('/wifi-')) return 'wifi';
   if (pathname === '/voucher' || pathname.startsWith('/voucher-'))
      return 'voucher-store';
   if (pathname === '/tab' || pathname.startsWith('/tab-')) return 'tab';
   if ((pathname === '/isp' || pathname.startsWith('/isp-')) && ISP_ENABLED)
      return 'isp';

   // Account pages are surface-agnostic.
   if (
      ['/subscription', '/billing', '/customization', '/api-integration'].some(
         (p) => pathname.startsWith(p)
      )
   ) {
      return null;
   }

   // Legacy URLs (Phase A — current production URLs).
   if (
      ['/vendo', '/data-plans', '/partners', '/distributor'].some(
         (p) => pathname === p
      )
   ) {
      return 'wifi';
   }
   if (
      ['/admin', '/stores', '/vouchers', '/transactions', '/payout'].some(
         (p) => pathname === p
      )
   ) {
      return 'voucher-store';
   }

   return null;
};
